import { ref } from 'vue';

import type { CallQueryParams } from '@stuut/api/query';

import { stuut } from '@/api';
import { sdk } from '@/sdk';
import { useFeatureFlags } from '@/store/featureFlags';
import { callListResponseToLegacyPage, callQueryParamsToSdkRequest } from '@/utils/callList';
import type { LegacyCallListPage } from '@/utils/callList';

export const CALLS_LIST_V2_FEATURE_FLAG = 'calls-list-v2';

const isCallsListV2Enabled = (flagValue: unknown): boolean => {
  return flagValue === true || flagValue === 'true' || flagValue === 'v2';
};

const callQueryParamsToV1Request = (params: CallQueryParams): Record<string, any> => {
  const requestParams = params.serialize({ forApiRequest: true });
  delete requestParams.sentiment;

  return requestParams;
};

export const useCallsList = () => {
  const featureFlagsStore = useFeatureFlags();
  const isUsingV2CallsList = ref(false);
  let featureFlagsPromise: Promise<Record<string, any>> | null = null;

  const loadFeatureFlags = () => {
    featureFlagsPromise ??= featureFlagsStore.loadFeatureFlags().catch((error) => {
      featureFlagsPromise = null;
      throw error;
    });

    return featureFlagsPromise;
  };

  const loadCallsListFlag = async () => {
    try {
      const flags = await loadFeatureFlags();
      isUsingV2CallsList.value = isCallsListV2Enabled(flags[CALLS_LIST_V2_FEATURE_FLAG]);
    } catch {
      isUsingV2CallsList.value = false;
    }

    return isUsingV2CallsList.value;
  };

  const listCalls = async (queryParams: CallQueryParams): Promise<LegacyCallListPage> => {
    if (await loadCallsListFlag()) {
      return callListResponseToLegacyPage(
        await sdk.calls.list(callQueryParamsToSdkRequest(queryParams)),
      );
    }

    const response = await stuut.calls.list(
      { params: callQueryParamsToV1Request(queryParams) },
      true,
    );

    return {
      current_page: response.current_page,
      data: response.data,
      next_page: response.next_page,
      total_records: response.total_records,
    };
  };

  void loadCallsListFlag();

  return { isUsingV2CallsList, listCalls };
};
